import { useState, useMemo } from 'react'
import { ChevronRight, ChevronDown, Users } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

interface MembroAlbero {
  id: number
  email: string
  nome: string
  tipo: string
  livello: string
  percentuale: number
  attivo: boolean
  sponsor_id: number | null
  depth: number
  stato: 'apc' | 'zero_canone' | 'inattivo' | 'attivo'
  is_apc: boolean
  in_zero_canone: boolean
  vq_rete: number
}

interface NetworkTreeProps {
  membri: MembroAlbero[]
  percentualeUtente: number
  onMemberClick?: (membro: MembroAlbero) => void
}

interface TreeNode extends MembroAlbero {
  children: TreeNode[]
}

const STATO_CONFIG = {
  apc: { 
    label: 'APC', 
    color: 'bg-green-500', 
    textColor: 'text-green-500',
    icon: '🟢',
    badgeClass: 'bg-green-600 hover:bg-green-700'
  },
  zero_canone: { 
    label: 'Zero Canone', 
    color: 'bg-yellow-500', 
    textColor: 'text-yellow-500',
    icon: '🟡',
    badgeClass: 'bg-yellow-600 hover:bg-yellow-700'
  },
  inattivo: { 
    label: 'Inattivo', 
    color: 'bg-red-500', 
    textColor: 'text-red-500',
    icon: '🔴',
    badgeClass: 'bg-red-600 hover:bg-red-700'
  },
  attivo: { 
    label: 'Attivo', 
    color: 'bg-blue-500', 
    textColor: 'text-blue-500',
    icon: '🔵',
    badgeClass: 'bg-blue-600 hover:bg-blue-700'
  }
}

function buildTree(membri: MembroAlbero[]): TreeNode[] {
  const membriByParent = new Map<number | null, MembroAlbero[]>()
  
  membri.forEach(membro => {
    const parentId = membro.sponsor_id
    if (!membriByParent.has(parentId)) {
      membriByParent.set(parentId, [])
    }
    membriByParent.get(parentId)!.push(membro)
  })
  
  function buildNodes(parentId: number | null): TreeNode[] {
    const children = membriByParent.get(parentId) || []
    return children.map(membro => ({
      ...membro,
      children: buildNodes(membro.id)
    }))
  }
  
  const directChildren = membri.filter(m => m.depth === 1)
  return directChildren.map(membro => ({
    ...membro,
    children: buildNodes(membro.id)
  }))
}

interface TreeRowProps {
  node: TreeNode
  level: number
  percentualeUtente: number
  expanded: Set<number>
  onToggle: (id: number) => void
  onMemberClick?: (membro: MembroAlbero) => void
  showOnlyApc: boolean
}

function hasApcInSubtree(node: TreeNode): boolean {
  if (node.is_apc) return true
  return node.children.some(child => hasApcInSubtree(child))
}

function TreeRow({ node, level, percentualeUtente, expanded, onToggle, onMemberClick, showOnlyApc }: TreeRowProps) {
  const hasChildren = node.children.length > 0
  const isExpanded = expanded.has(node.id)
  const statoConfig = STATO_CONFIG[node.stato]
  
  const diffProvvigionale = Math.max(0, percentualeUtente - node.percentuale)
  
  const filteredChildren = showOnlyApc 
    ? node.children.filter(c => hasApcInSubtree(c))
    : node.children
  
  if (showOnlyApc && !hasApcInSubtree(node)) {
    return null
  }
  
  return (
    <>
      <div 
        className={cn(
          "flex items-center gap-2 py-2 px-3 hover:bg-slate-700/50 rounded-lg cursor-pointer transition-colors",
          level === 0 && "bg-slate-700/30"
        )}
        style={{ paddingLeft: `${level * 24 + 12}px` }}
        onClick={() => onMemberClick?.(node)}
      >
        <button
          className="w-5 h-5 flex items-center justify-center text-slate-400 hover:text-white"
          onClick={(e) => {
            e.stopPropagation()
            if (hasChildren) onToggle(node.id)
          }}
        >
          {hasChildren ? (
            isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
          ) : (
            <span className="w-4" />
          )}
        </button>
        
        <div className={cn("w-2.5 h-2.5 rounded-full flex-shrink-0", statoConfig.color)} />
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{node.nome}</span>
            <Badge className={cn("text-xs", statoConfig.badgeClass)}>
              {statoConfig.label}
            </Badge>
          </div>
          {node.tipo === 'consulente' || node.tipo === 'cliente_consulente' ? (
            <div className="flex items-center gap-2 mt-1">
              <Progress value={Math.min(100, (node.vq_rete / 6) * 100)} className="h-1 w-20" />
              <span className="text-xs text-slate-400">{node.vq_rete} VQ</span>
            </div>
          ) : null}
        </div>
        
        <div className="flex items-center gap-4 text-sm">
          <div className="text-center min-w-[60px]">
            <span className="text-slate-400">{node.livello}</span>
            <span className="text-slate-500 ml-1">({node.percentuale}%)</span>
          </div>
          
          <div className="text-center min-w-[50px]">
            <span className="font-medium">{node.vq_rete}</span>
            <span className="text-slate-500 text-xs ml-1">VQ</span>
          </div>
          
          {diffProvvigionale > 0 && (
            <div className="text-center min-w-[60px]">
              <span className="text-green-500 font-medium">+{diffProvvigionale.toFixed(1)}%</span>
            </div>
          )}
        </div>
      </div>
      
      {isExpanded && filteredChildren.map(child => (
        <TreeRow
          key={child.id}
          node={child}
          level={level + 1}
          percentualeUtente={percentualeUtente}
          expanded={expanded}
          onToggle={onToggle}
          onMemberClick={onMemberClick}
          showOnlyApc={showOnlyApc}
        />
      ))}
    </>
  )
}

export function NetworkTree({ membri, percentualeUtente, onMemberClick }: NetworkTreeProps) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [showOnlyApc, setShowOnlyApc] = useState(false)
  
  const tree = useMemo(() => buildTree(membri), [membri])
  
  const toggleNode = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }
  
  const expandAll = () => {
    setExpanded(new Set(membri.map(m => m.id)))
  }
  
  const collapseAll = () => {
    setExpanded(new Set())
  }
  
  const stats = useMemo(() => {
    const totale = membri.length
    const apc = membri.filter(m => m.is_apc).length
    const zeroCanone = membri.filter(m => m.in_zero_canone).length
    const inattivi = membri.filter(m => !m.attivo).length
    return { totale, apc, zeroCanone, inattivi }
  }, [membri])
  
  if (membri.length === 0) {
    return (
      <div className="text-center py-8">
        <Users className="w-12 h-12 mx-auto mb-4 text-slate-500" />
        <p className="text-slate-400">Non hai ancora membri nella tua rete</p>
        <p className="text-sm text-slate-500 mt-1">
          Condividi il tuo link di invito per costruire la tua rete
        </p>
      </div>
    )
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm">
          <span className="text-slate-400">
            <span className="font-medium text-white">{stats.totale}</span> membri
          </span>
          <span className="text-green-500">
            <span className="font-medium">{stats.apc}</span> APC
          </span>
          <span className="text-gray-400">
            <span className="font-medium">{stats.zeroCanone}</span> Zero Canone
          </span>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">Solo APC</span>
            <Switch checked={showOnlyApc} onCheckedChange={setShowOnlyApc} />
          </div>
          <div className="flex gap-2">
            <button 
              onClick={expandAll}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              Espandi tutto
            </button>
            <span className="text-slate-600">|</span>
            <button 
              onClick={collapseAll}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              Chiudi tutto
            </button>
          </div>
        </div>
      </div>
      
      <div className="bg-slate-800/30 rounded-lg border border-slate-700/50">
        <div className="grid grid-cols-[1fr_80px_60px_70px] gap-2 px-4 py-2 border-b border-slate-700/50 text-xs text-slate-400 font-medium">
          <span>Membro</span>
          <span className="text-center">Livello</span>
          <span className="text-center">VQ</span>
          <span className="text-center">Diff. %</span>
        </div>
        
        <div className="divide-y divide-slate-700/30">
          {tree.map(node => (
            <TreeRow
              key={node.id}
              node={node}
              level={0}
              percentualeUtente={percentualeUtente}
              expanded={expanded}
              onToggle={toggleNode}
              onMemberClick={onMemberClick}
              showOnlyApc={showOnlyApc}
            />
          ))}
        </div>
      </div>
      
      <div className="flex items-center gap-4 text-xs text-slate-500">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500" /> APC (genera VQ)
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-yellow-400" /> Zero Canone
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-red-500" /> Inattivo
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-blue-500" /> Attivo
        </span>
      </div>
    </div>
  )
}
